import { useMemo } from 'react';

import { useTheme } from '@mui/material';

import { useQuery } from '@tanstack/react-query';

import { UserInfo } from '@/api/data-contracts';
import { useUsersService } from '@/features/users';
import { queryKeys } from '@/react-query/constants';
import { getModuleAccentColor } from '@/shared/lib/hooks/useModuleChipColors';

import { formatRole, getPlatformChipInfo } from './commentUtils';

const useAvatarColor = (platform: string | undefined): string => {
  const { palette } = useTheme();
  const platformChipInfo = getPlatformChipInfo(platform);
  return platformChipInfo
    ? getModuleAccentColor(palette, platformChipInfo.userType)
    : palette.accent.main;
};

interface UseAuthorInfoParams {
  authorId: string;
  authorUserType?: string;
  authorPosition?: string;
  allUsers: UserInfo[];
}

const useAuthorInfo = ({
  authorId,
  authorUserType,
  authorPosition,
  allUsers,
}: UseAuthorInfoParams) => {
  const { getUserDetails } = useUsersService();

  const { storedPlatform, storedPosition } = useMemo(() => {
    const user = allUsers.find((u) => u.id === authorId);
    return {
      storedPlatform: authorUserType ?? user?.platforms?.[0],
      storedPosition: authorPosition ?? user?.position,
    };
  }, [allUsers, authorId, authorUserType, authorPosition]);

  const { data: authorUserInfo } = useQuery<UserInfo | null>(
    [queryKeys.user, authorId],
    async ({ signal }) => {
      try {
        return await getUserDetails(authorId, signal);
      } catch {
        return null;
      }
    },
    { enabled: (!storedPlatform || !storedPosition) && !!authorId },
  );

  const authorPlatform = storedPlatform ?? authorUserInfo?.platforms?.[0];
  const avatarColor = useAvatarColor(authorPlatform);

  const authorRole = useMemo(
    () => formatRole(storedPosition || authorUserInfo?.position),
    [storedPosition, authorUserInfo?.position],
  );

  const platformChipInfo = useMemo(() => getPlatformChipInfo(authorPlatform), [authorPlatform]);

  return { avatarColor, authorRole, platformChipInfo };
};

export default useAuthorInfo;
